<!-- Note this file is from new workapckage screen which we don't support for now -->
<template>
  <v-card class="mb-2" elevation="1">
    <!-- Header section - always visible -->
    <div class="workpackage-header pa-2 px-4 d-flex justify-space-between align-center">
      <!-- Left side with title and metadata -->
      <div class="d-flex align-center">
        <div v-if="workpackage.mocked" class="progress-spinner-container mr-2">
          <progress-spinner
            :job-id="workpackage.description"
            :job-type="workpackage.isCloneJob ? 'run_clone_workpackage' : 'run_create_workpackage'"
          />
        </div>
        <a
          class="workpackage-title font-weight-bold mr-6"
          e2e-slug="linkWPNew"
          @click="selectWorkpackageHandle"
          >{{ workpackage.description }}</a
        >
        <span class="workpackage-meta">
          <span class="meta-label">{{ $t('workPackages.sourceWorkpackage') }}:</span>
          <span class="meta-value">
            <tooltipped-truncated-field
              :text="
                workpackage.sourceWorkpackageDescription === undefined
                  ? workpackage.storeFormat
                  : workpackage.sourceWorkpackageDescription
              "
              :truncation-length="35"
            />
          </span>
          <feature-toggle toggle="enableWorkpackagePeriodTypes">
            <template v-if="periodTypeBadge">
              <span class="mx-2">|</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span class="period-type-badge mr-1" v-bind="attrs" v-on="on">{{
                    periodTypeBadge.label
                  }}</span>
                </template>
                <span>{{ periodTypeBadge.tooltip }}</span>
              </v-tooltip>
            </template>
          </feature-toggle>
          <feature-toggle toggle="enableReferenceDate">
            <span v-if="!periodTypeBadge" class="mx-2">|</span>
            <span class="meta-label">{{ $t('workPackages.referenceDate') }}:</span>
            <span class="meta-value">{{ getReferenceDate(workpackage.referenceDate) }}</span>
          </feature-toggle>
          <span class="mx-2">|</span>
          <span class="meta-label">{{ $t('workPackages.completionDate') }}:</span>
          <span class="meta-value">{{ formatLocalisedDate(workpackage.completionDate) }}</span>
          <span class="mx-1">/</span>
          <span class="meta-label">{{ $t('workPackages.goLiveDate') }}:</span>
          <span class="meta-value">{{ formatLocalisedDate(workpackage.goLiveDate) }}</span>
          <span class="mx-2">|</span>
          <span class="meta-label">{{ $t('workPackages.owner') }}:</span>
          <span class="meta-value">{{ workpackage.owner }}</span>
        </span>
      </div>

      <!-- Right side with action buttons -->
      <div class="d-flex">
        <v-btn
          v-if="isCreateWorkpackageFromCopyEnabled && allowCopy && canEditWorkpackage"
          :disabled="disabled()"
          icon
          small
          class="header-icon-btn"
        >
          <v-icon small @click="emitOpenWorkpackageCopyDialog">mdi-content-copy</v-icon>
        </v-btn>
        <v-btn
          v-if="workpackage.type !== workpackageTypes.master"
          :disabled="disabled()"
          :loading="deleting()"
          e2e-slug="btnDeleteWPNew"
          icon
          small
          class="header-icon-btn"
          @click="emitOpenDeleteDialog"
        >
          <v-icon small>mdi-trash-can-outline</v-icon>
        </v-btn>
        <v-btn icon small class="header-icon-btn" @click="toggleExpanded">
          <v-icon small>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Collapsed content - only visible when not expanded -->
    <div v-if="!isExpanded" class="collapsed-pricing-groups px-2">
      <workpackage-pricing-groups :workpackage="workpackage" :collapsed-view="true" />
    </div>

    <!-- Expanded content - only visible when expanded -->
    <v-expand-transition>
      <div v-if="isExpanded" class="workpackage-expanded-content-wrapper px-4">
        <div class="workpackage-expanded-content">
          <v-row no-gutters class="expanded-container">
            <v-col class="workpackage-info-container sidebar-col my-4 pr-4">
              <workpackage-info
                :workpackage="workpackage"
                :store-group="storeGroup"
                :select-workpackage="selectWorkpackageHandle"
              />
              <workpackage-ao-extracts :workpackage="workpackage" />
            </v-col>
            <v-col class="pricing-groups-container content-col">
              <div class="pl-4 py-4">
                <div class="grey--text text-center">
                  <workpackage-pricing-groups :workpackage="workpackage" />
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
        <div class="workpackage-footer pa-4">
          <v-row no-gutters class="expanded-container justify-end">
            <feature-toggle toggle="displayExportPenaltyDataButton">
              <v-btn
                small
                color="primary"
                :loading="loadingPenaltyExports"
                class="ml-2"
                depressed
                @click="
                  exportPenaltyData({
                    workpackage: workpackage,
                  })
                "
              >
                {{ $t('actions.downloadPenaltyData') }}
              </v-btn>
            </feature-toggle>
            <feature-toggle toggle="displayExportToSAPButton">
              <div class="api-export-container d-inline-flex align-center">
                <div class="last-export-info mr-2">
                  <div
                    v-if="hasExportInfo(workpackage._id, exportType.exportToSAP)"
                    class="d-flex align-center"
                  >
                    {{ getExportLastText(workpackage._id, exportType.exportToSAP) }}
                  </div>
                </div>
                <sap-export-button
                  :disabled="disabled() || disableOWUserExport"
                  :loading="exportingToSAP()"
                  :params="sapExportParams"
                  @downloadExportToSAP="emitTriggerDownloadExportToSap"
                />
              </div>
            </feature-toggle>
            <feature-toggle toggle="displayExportToAPIButton">
              <div class="api-export-container d-inline-flex align-center">
                <div class="last-export-info mr-2">
                  <div v-if="hasExportInfo(workpackage._id)" class="d-flex align-center">
                    {{ getExportLastText(workpackage._id) }}
                  </div>
                </div>
                <v-btn
                  small
                  :disabled="disabled() || disableOWUserExport"
                  :loading="posting()"
                  color="primary"
                  class="ml-2"
                  e2e-slug="btnPostPricingNew"
                  depressed
                  @click="emitOpenExportAPIDialog"
                >
                  {{ $t('actions.postPricing') }}
                </v-btn>
              </div>
            </feature-toggle>
            <span v-if="displayGoLiveDateDialog">
              <v-btn
                small
                :disabled="disabled()"
                :loading="exporting()"
                color="primary"
                outlined
                class="ml-2"
                e2e-slug="btnExportPricingNew"
                depressed
                @click="emitOpenExportPricingDialog"
              >
                {{ $t('actions.downloadPricing') }}
              </v-btn>
            </span>
            <span v-else-if="!isXSRClient">
              <!-- XSR export uses downloadFile option in sap-export-button -->
              <v-btn
                small
                :disabled="disabled()"
                :loading="exporting()"
                color="primary"
                outlined
                class="ml-2"
                e2e-slug="btnExportPricingNew"
                depressed
                @click="
                  downloadWorkpackageResults({
                    selectedWorkpackage: workpackage,
                    fields: fields,
                    exportType: exportType.exportPricing,
                    columnFormatters: columnFormatters,
                  })
                "
              >
                {{ $t('actions.downloadPricing') }}
              </v-btn>
            </span>
            <v-btn
              small
              :disabled="disabled()"
              :loading="transferring()"
              class="ml-2"
              color="primary"
              e2e-slug="btnTransferWorkpackageNew"
              depressed
              @click="emitOpenTransferDialog"
            >
              {{ $t('workPackages.transferWorkpackage') }}
            </v-btn>
          </v-row>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import to from 'await-to-js';
import { mapState, mapGetters, mapActions } from 'vuex';
import exportType from '@enums/export-type';
import sapExportUtilsMixin from '../../../mixins/sapExportUtils';
import featureFlagsMixin from '../../../mixins/featureFlags';
import {
  showGoLiveDateDialog,
  owUsersCanExport,
  createWorkpackageFromCopy,
} from '@enums/feature-flags';
import workpackageTypes from '@enums/workpackage-types';
import workpackagePeriodTypes from '@enums/workpackage-period-types';

export default {
  name: 'WorkpackageItem',
  mixins: [sapExportUtilsMixin, featureFlagsMixin],
  props: {
    initialExpanded: {
      type: Boolean,
      default: false,
    },
    workpackage: {
      type: Object,
      required: true,
    },
    allowCopy: {
      type: Boolean,
      default: false,
    },
    hasExportInfo: {
      type: Function,
      required: true,
    },
    getExportLastText: {
      type: Function,
      required: true,
    },
    columnFormatters: {
      type: Object,
      required: true,
    },
    fields: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isExpanded: this.initialExpanded,
      loadingPenaltyExports: false,
      exportType,
      workpackageTypes,
    };
  },
  computed: {
    ...mapState('clientConfig', ['i18nconfig', 'exportConfigs', 'toggleLogic']),
    ...mapState('workpackages', [
      'deletingIds',
      'exportingIds',
      'postingIds',
      'exportingToSAPIds',
      'refreshingIds',
    ]),
    ...mapGetters('workpackages', ['isAnyWorkpackageInTransferList']),
    ...mapGetters('context', ['isOWUser', 'canEditWorkpackage']),
    storeGroup() {
      return this.formatStoreFront(this.workpackage.storeFormat);
    },
    exportToSAPFields() {
      return this.getSapExportFields();
    },
    displayGoLiveDateDialog() {
      return !!this.toggleLogic[showGoLiveDateDialog];
    },
    disableOWUserExport() {
      return !this.isFeatureFlagEnabled(owUsersCanExport) && this.isOWUser;
    },
    isCreateWorkpackageFromCopyEnabled() {
      return this.isFeatureFlagEnabled(createWorkpackageFromCopy);
    },
    isXSRClient() {
      return this.toggleLogic.workpackageExportFunction === 'exportXSRWorkpackage';
    },
    periodTypeBadge() {
      const periodType = this.workpackage.workpackagePeriodType;
      if (periodType === workpackagePeriodTypes.weekly) {
        return {
          label: this.$t('workPackages.periodTypes.weeklyShort'),
          tooltip: this.$t('workPackages.periodTypes.weekly'),
        };
      }
      if (periodType === workpackagePeriodTypes.aoPeriod) {
        return {
          label: this.$t('workPackages.periodTypes.aoPeriodShort'),
          tooltip: this.$t('workPackages.periodTypes.aoPeriod'),
        };
      }
      return null;
    },
    sapExportParams() {
      return {
        selectedWorkpackage: this.workpackage,
        fieldConfigs: this.isXSRClient ? null : this.exportToSAPFields, // XSR: backend determines fields, others: use SAP field configs
        exportConfig: this.exportConfigs.exportToSAP,
        exportType: exportType.exportToSAP,
      };
    },
  },

  methods: {
    ...mapActions('workpackages', [
      'downloadWorkpackageResults',
      'downloadXSROutboundExport',
      'downloadPenaltyExportData',
    ]),
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    formatStoreFront(storeFront) {
      const hierarchyLevel0 = this.$t('hierarchy.hierarchyLevels.0');
      const nStart = storeFront.indexOf(hierarchyLevel0);
      const nEnd = nStart + hierarchyLevel0.length;

      if (nStart === -1) {
        return storeFront;
      }

      return `${storeFront.slice(0, nStart)}${storeFront.slice(nStart, nEnd)}${storeFront.slice(
        nEnd
      )}`;
    },
    selectWorkpackageHandle() {
      this.$emit('selectWorkpackage', this.workpackage);
    },
    async exportPenaltyData() {
      this.loadingPenaltyExports = true;
      const [err] = await to(this.downloadPenaltyExportData({ workpackage: this.workpackage }));
      if (err) this.loadingPenaltyExports = false;
      this.loadingPenaltyExports = false;
    },
    deleting() {
      return this.deletingIds.includes(this.workpackage._id);
    },
    transferring() {
      return this.isAnyWorkpackageInTransferList([this.workpackage._id]);
    },
    exportingToSAP() {
      return this.exportingToSAPIds.includes(this.workpackage._id);
    },
    exporting() {
      return this.exportingIds.includes(this.workpackage._id);
    },
    posting() {
      return this.postingIds.includes(this.workpackage._id);
    },
    refreshing() {
      return this.refreshingIds.includes(this.workpackage._id);
    },
    disabled() {
      return (
        this.deleting() ||
        this.exporting() ||
        this.posting() ||
        this.refreshing() ||
        this.workpackage.mocked
      );
    },
    emitOpenWorkpackageCopyDialog() {
      this.$emit('open-workpackage-copy-dialog', this.workpackage);
    },
    emitOpenDeleteDialog() {
      this.$emit('open-delete-dialog', this.workpackage);
    },
    emitOpenTransferDialog() {
      this.$emit('open-transfer-dialog', this.workpackage);
    },
    emitOpenExportAPIDialog() {
      this.$emit('open-export-api-dialog', this.workpackage);
    },
    emitOpenExportPricingDialog() {
      this.$emit('open-export-pricing-dialog', this.workpackage);
    },
    emitTriggerDownloadExportToSap(event) {
      this.$emit('trigger-download-export-to-sap', event);
    },
    getReferenceDate(referenceDate) {
      return referenceDate ? this.formatLocalisedDate(referenceDate) : referenceDate;
    },
  },
};
</script>

<style lang="scss" scoped>
.workpackage-header {
  background: #fff;
  color: #343a3f;
  min-height: 48px;
}

.workpackage-footer {
  background: #fff;
  border-top: 1px solid #a2a9b0;
}

.workpackage-title {
  color: #023b6e;
  font-size: 1.4rem;
}

.workpackage-meta {
  color: #343a3f;
  font-weight: 400;
  font-size: 1.2rem;
  .meta-label {
    font-weight: 400;
    color: #343a3f;
    margin-right: 2px;
  }
  .meta-value {
    font-weight: 700;
    color: #343a3f;
    margin-right: 6px;
  }
}

.header-icon-btn {
  color: #023b6e !important;
  margin-left: 4px;
}

.period-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  min-width: 3.2rem;
  padding: 0.2rem 0.6rem;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1.15;
  color: #343a3f;
  background: #eceff1;
  border: 1px solid #cfd4d9;
  border-radius: 4px;
  cursor: default;
  vertical-align: text-bottom;
}

.workpackage-expanded-content-wrapper {
  background: #fff;
  border-top: 1px solid #a2a9b0;
}

.workpackage-expanded-content {
  background: #fff;
}

.workpackage-info-container {
  max-width: 400px;
  flex: 0 0 400px;
  border-right: 1px solid #a2a9b0;
}

.pricing-groups-container {
  flex: 1 1 0;
  max-width: 100%;
}

.collapsed-pricing-groups {
  background-color: #fff;
}

.collapsed-pricing-group {
  background-color: rgba(215, 222, 231, 0.5);
  border-radius: 4px;
  display: inline-flex;
}

.progress-spinner-container {
  .v-progress-circular {
    width: 24px !important;
    height: 24px !important;
  }
}
</style>
